<template>
  <div class="flex-1 flex flex-col min-w-0">
    <div class="flex items-center gap-1 mb-2">
      <button
        type="button"
        class="flex items-center justify-center w-6 h-6 hover:bg-gray-lighter rounded-full transition-colors"
        :disabled="isLoadingComments"
        :title="$t('brand-surveys.survey-question-modal.refresh-comments')"
        @click="$emit('refresh-comments')"
      >
        <icon
          :icon="mdiRefresh"
          :size="IconSize.SM"
          :class="{ 'animate-spin': isLoadingComments }"
        />
      </button>
      <small
        class="small flex-shrink-0 text-gray-darker leading-none"
        style="font-size: 11px; text-transform: uppercase; font-weight: 700"
      >
        {{ $t('brand-surveys.survey-question-modal.comments') }}
      </small>
    </div>

    <div
      id="comments-container"
      class="border-solid border border-gray-light flex-1 min-h-0 overflow-y-auto comments-container relative"
    >
      <div
        v-if="isDeletingComment"
        class="absolute inset-0 bg-white bg-opacity-75 flex justify-center items-center z-10"
      >
        <icon-spinner class="stroke-primary" />
      </div>

      <infinite-data
        :url="commentsUrl"
        :query="commentsQuery"
        direction="top"
        container-selector="#comments-container"
        :should-fetch="shouldFetchComments"
        :force-update="forceUpdate"
        api-v2
        @data-deliver="handleCommentsData"
      >
        <template #default="{ items }">
          <div class="break-words overflow-hidden">
            <survey-question-comment
              v-for="comment in items"
              :key="comment.id"
              :comment="comment"
              :is-being-edited="
                editingComment && editingComment.id === comment.id
              "
              @delete-comment="$emit('delete-comment', $event)"
              @edit-comment="$emit('edit-comment', $event)"
            />
          </div>
        </template>

        <template #no-results>
          <div
            class="w-full h-full flex justify-center items-center min-h-48 absolute top-0 left-0 px-2 text-center"
          >
            <span class="italic text-gray-darker">
              {{ $t('brand-surveys.survey-question-modal.no-data') }}
            </span>
          </div>
        </template>
      </infinite-data>
    </div>

    <div class="border border-gray-light rounded-b flex-shrink-0">
      <div
        v-if="editingComment"
        class="p-2 bg-blue-lightest border-b border-gray-light flex justify-between items-center"
      >
        <span class="text-sm text-blue">
          {{ $t('brand-surveys.survey-question-modal.editing-comment') }}
        </span>
        <btn type="button" size="xs" class="px-2" @click="$emit('cancel-edit')">
          {{ $t('common.cancel') }}
        </btn>
      </div>
      <wysiwyg-input
        :value="content"
        height="textarea-wysiwyg"
        dense
        borderless
        :hidden-actions="['attachment', 'mention', 'link']"
        @input="$emit('input', $event)"
      />
    </div>
  </div>
</template>

<script>
import { defineComponent } from '@vue/composition-api';
import SurveyQuestionComment from './SurveyQuestionComment.vue';
import { WysiwygInput } from '@/components/WysiwygInput';
import Btn from '@/components/Button/Button.vue';
import IconSpinner from '@/components/Icon/icons/IconSpinner.vue';
import { Icon, IconSize } from '@/components/Icon';
import { mdiRefresh } from '@mdi/js';
import InfiniteData from '@/components/DataProvider/InfiniteData.vue';

export default defineComponent({
  name: 'SurveyQuestionComments',
  components: {
    SurveyQuestionComment,
    WysiwygInput,
    Btn,
    IconSpinner,
    Icon,
    InfiniteData,
  },
  props: {
    comments: { type: Array, default: () => [] },
    isLoadingComments: { type: Boolean, default: false },
    isDeletingComment: { type: Boolean, default: false },
    editingComment: { type: Object, default: null },
    content: { type: String, default: '' },
    commentsUrl: { type: String, required: true },
    commentsQuery: { type: Object, default: () => ({}) },
    shouldFetchComments: { type: Boolean, default: true },
    forceUpdate: { type: [Boolean, Number], default: false },
  },
  model: {
    prop: 'content',
    event: 'input',
  },
  data() {
    return {
      isFirstLoad: true,
    };
  },
  methods: {
    handleCommentsData(comments) {
      this.$emit('comments-loaded', comments);

      if (this.isFirstLoad && comments.length > 0) {
        this.isFirstLoad = false;
        this.$nextTick(() => {
          const container = document.getElementById('comments-container');
          if (container) {
            container.scrollTop = container.scrollHeight;
          }
        });
      }
    },
    resetFirstLoad() {
      this.isFirstLoad = true;
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const container = document.getElementById('comments-container');
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      });
    },
  },
  watch: {
    shouldFetchComments(newValue) {
      if (newValue) {
        this.isFirstLoad = true;
      }
    },
  },
  setup() {
    return {
      mdiRefresh,
      IconSize,
    };
  },
});
</script>

<style scoped>
.small {
  font-size: 11px;
  text-transform: uppercase;
  font-weight: 700;
  @apply text-gray-darker;
}

.comments-container {
  border-bottom: none;
  @apply rounded-t;
}
</style>
